import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import AuthService from "../../services/auth.service";

const errorMessage = (error) =>
    error.response?.data?.message || error.message || "Something went wrong";

// Admin Login
export const adminLogin = createAsyncThunk(
    "auth/adminLogin",
    async (credentials, thunkAPI) => {
        try {
            return await AuthService.adminLogin(credentials);
        } catch (error) {
            return thunkAPI.rejectWithValue(errorMessage(error));
        }
    }
);

// User Login
export const userLogin = createAsyncThunk(
    "auth/userLogin",
    async (credentials, thunkAPI) => {
        try {
            return await AuthService.userLogin(credentials);
        } catch (error) {
            return thunkAPI.rejectWithValue(errorMessage(error));
        }
    }
);

// Get Admin Profile
export const getProfile = createAsyncThunk(
    "auth/getProfile",
    async (_, thunkAPI) => {
        try {
            const response = await AuthService.getProfile();
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(errorMessage(error));
        }
    }
);

// Get Dashboard
export const getDashboard = createAsyncThunk(
    "auth/getDashboard",
    async (_, thunkAPI) => {
        try {
            const response = await AuthService.getDashboard();

            console.log("DASHBOARD RESPONSE:", response);

            if (!response || typeof response !== "object") {
                return thunkAPI.rejectWithValue(
                    "Invalid dashboard response"
                );
            }

            return response;
        } catch (error) {
            console.error(
                "DASHBOARD API ERROR:",
                error.response?.data || error.message
            );

            return thunkAPI.rejectWithValue(errorMessage(error));
        }
    }
);

// Clear Election Data
export const clearElectionData = createAsyncThunk(
    "auth/clearElectionData",
    async (_, thunkAPI) => {
        try {
            const response = await AuthService.clearElectionData();

            console.log("CLEAR ELECTION DATA RESPONSE:", response);

            return response;
        } catch (error) {
            console.error(
                "CLEAR ELECTION DATA ERROR:",
                error.response?.data || error.message
            );

            return thunkAPI.rejectWithValue(errorMessage(error));
        }
    }
);

// Logout
export const logout = createAsyncThunk(
    "auth/logout",
    async () => {
        AuthService.logout();
        return true;
    }
);

const initialState = {
    loading: false,
    dashboardLoading: false,
    clearElectionDataLoading: false,

    error: "",
    dashboardError: "",
    clearElectionDataError: "",

    user: AuthService.getUserDetails(),
    dashboard: null,
    clearElectionDataResult: null,
};

const startLoading = (state) => {
    state.loading = true;
    state.error = "";
};

const failLoading = (state, action) => {
    state.loading = false;
    state.error =
        action.payload || action.error?.message || "Login failed";
};

const authSlice = createSlice({
    name: "auth",
    initialState,

    reducers: {
        clearAuthError(state) {
            state.error = "";
        },

        clearDashboardError(state) {
            state.dashboardError = "";
        },

        clearElectionDataError(state) {
            state.clearElectionDataError = "";
        },

        clearElectionDataResult(state) {
            state.clearElectionDataResult = null;
        },
    },

    extraReducers: (builder) => {
        builder
            // Admin Login
            .addCase(adminLogin.pending, startLoading)
            .addCase(adminLogin.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
            })
            .addCase(adminLogin.rejected, failLoading)

            // User Login
            .addCase(userLogin.pending, startLoading)
            .addCase(userLogin.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
            })
            .addCase(userLogin.rejected, failLoading)

            // Profile
            .addCase(getProfile.pending, (state) => {
                state.error = "";
            })
            .addCase(getProfile.fulfilled, (state, action) => {
                state.user = action.payload;
            })
            .addCase(getProfile.rejected, (state, action) => {
                state.error =
                    action.payload || action.error?.message;
            })

            // Dashboard
            .addCase(getDashboard.pending, (state) => {
                state.dashboardLoading = true;
                state.dashboardError = "";
            })
            .addCase(getDashboard.fulfilled, (state, action) => {
                state.dashboardLoading = false;
                state.dashboard = action.payload;
            })
            .addCase(getDashboard.rejected, (state, action) => {
                state.dashboardLoading = false;
                state.dashboardError =
                    action.payload ||
                    action.error?.message ||
                    "Failed to fetch dashboard";
            })

            // Clear Election Data
            .addCase(clearElectionData.pending, (state) => {
                state.clearElectionDataLoading = true;
                state.clearElectionDataError = "";
                state.clearElectionDataResult = null;
            })
            .addCase(clearElectionData.fulfilled, (state, action) => {
                state.clearElectionDataLoading = false;
                state.clearElectionDataResult = action.payload;

                // Clear dashboard data after successful deletion
                state.dashboard = null;
            })
            .addCase(clearElectionData.rejected, (state, action) => {
                state.clearElectionDataLoading = false;
                state.clearElectionDataError =
                    action.payload ||
                    action.error?.message ||
                    "Failed to clear election data";
            })

            // Logout
            .addCase(logout.fulfilled, (state) => {
                state.user = null;
                state.dashboard = null;
                state.error = "";
                state.dashboardError = "";
                state.clearElectionDataError = "";
                state.clearElectionDataResult = null;
            });
    },
});

export const {
    clearAuthError,
    clearDashboardError,
    clearElectionDataError,
    clearElectionDataResult,
} = authSlice.actions;

export default authSlice.reducer;